import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  Chip,
  Pagination,
  Tabs,
  Tab,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { useRecoilValue } from "recoil";
import { loginUserSelector } from "../recoil/selectors/loginUserSelector";
import { useApiGetOperationLogs } from "../hooks/api/useApiGetOperationLogs";
import {
  ResponseOperationLogResponse,
  ResponseItemStatusHistoryResponse,
} from "../services/OpenAPI";

const ACTION_LABELS: Record<string, string> = {
  item_status_change: "ステータス変更",
  item_restore: "商品復元",
  item_register: "商品登録",
  item_bulk_register: "CSV取り込み登録",
  item_update: "商品情報更新",
};

// ---- 操作ログタブ ----

const OperationLogRow: React.FC<{ log: ResponseOperationLogResponse }> = ({ log }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>{log.id}</TableCell>
        <TableCell>{log.userName}</TableCell>
        <TableCell>
          <Chip label={ACTION_LABELS[log.action] ?? log.action} size="small" color="primary" variant="outlined" />
        </TableCell>
        <TableCell>{log.targetCount} 件</TableCell>
        <TableCell>{new Date(log.createdAt).toLocaleString("ja-JP")}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={6} sx={{ py: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                ステータス変遷
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>商品名</TableCell>
                    <TableCell>変更前</TableCell>
                    <TableCell>変更後</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(log.statusChanges ?? []).map((sc, i) => (
                    <TableRow key={i}>
                      <TableCell>{sc.amazonItemName}</TableCell>
                      <TableCell>
                        <Chip label={sc.fromStatusName} size="small" sx={{ bgcolor: "#f5f5f5" }} />
                      </TableCell>
                      <TableCell>
                        <Chip label={sc.toStatusName} size="small" color="success" variant="outlined" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// ---- アイテム遷移タブ ----

const ItemHistoryRow: React.FC<{ item: ResponseItemStatusHistoryResponse }> = ({ item }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>{item.amazonItemId}</TableCell>
        <TableCell>{item.amazonItemName}</TableCell>
        <TableCell>{item.history?.length ?? 0} 回</TableCell>
        <TableCell>
          {item.history?.length > 0
            ? `${item.history[0].fromStatusName} → ${item.history[item.history.length - 1].toStatusName}`
            : "-"}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={5} sx={{ py: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                ステータス変遷履歴
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>変更日時</TableCell>
                    <TableCell>操作者</TableCell>
                    <TableCell>変更前</TableCell>
                    <TableCell>変更後</TableCell>
                    <TableCell>操作種別</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(item.history ?? []).map((h, i) => (
                    <TableRow key={i}>
                      <TableCell>{new Date(h.changedAt).toLocaleString("ja-JP")}</TableCell>
                      <TableCell>{h.userName}</TableCell>
                      <TableCell>
                        <Chip label={h.fromStatusName} size="small" sx={{ bgcolor: "#f5f5f5" }} />
                      </TableCell>
                      <TableCell>
                        <Chip label={h.toStatusName} size="small" color="success" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip label={ACTION_LABELS[h.action] ?? h.action} size="small" variant="outlined" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// ---- メインページ ----

const OperationLogs: React.FC = () => {
  const loginUser = useRecoilValue(loginUserSelector);
  const { operationLogs, itemHistory, fetchOperationLogs, fetchItemStatusHistory, isLoading, error } =
    useApiGetOperationLogs();
  const [tab, setTab] = useState(0);
  const [logPage, setLogPage] = useState(1);
  const [itemPage, setItemPage] = useState(1);
  const limit = 20;

  useEffect(() => {
    if (!loginUser?.tenantId) return;
    if (tab === 0) fetchOperationLogs(loginUser.tenantId, logPage, limit);
    else fetchItemStatusHistory(loginUser.tenantId, itemPage, limit);
  }, [loginUser?.tenantId, tab, logPage, itemPage]);

  if (!loginUser?.isOperationLogViewer) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="error">このページを閲覧する権限がありません。</Typography>
      </Box>
    );
  }

  const logTotalPages = Math.ceil((operationLogs?.total ?? 0) / limit);
  const itemTotalPages = Math.ceil((itemHistory?.total ?? 0) / limit);

  return (
    <Box
      sx={{
        backgroundColor: "white",
        minHeight: "100vh",
        maxHeight: "100vh",
        overflowY: "auto",
        p: 3,
        boxSizing: "border-box",
      }}
    >
      <Typography variant="h5" sx={{ mb: 2, fontWeight: "bold" }}>
        操作ログ
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="操作ログ" />
        <Tab label="アイテム遷移" />
      </Tabs>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {/* 操作ログタブ */}
      {tab === 0 && (
        <>
          <TableContainer component={Paper}>
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f9fd" }}>
                  <TableCell />
                  <TableCell>ID</TableCell>
                  <TableCell>操作者</TableCell>
                  <TableCell>操作種別</TableCell>
                  <TableCell>対象件数</TableCell>
                  <TableCell>操作日時</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">読み込み中...</TableCell>
                  </TableRow>
                ) : (operationLogs?.logs ?? []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">データがありません</TableCell>
                  </TableRow>
                ) : (
                  (operationLogs?.logs ?? []).map((log) => (
                    <OperationLogRow key={log.id} log={log} />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mt: 2, mb: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 2 }}>
              全 {operationLogs?.total ?? 0} 件
            </Typography>
            <Pagination
              count={logTotalPages}
              page={logPage}
              onChange={(_, v) => setLogPage(v)}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        </>
      )}

      {/* アイテム遷移タブ */}
      {tab === 1 && (
        <>
          <TableContainer component={Paper}>
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f9fd" }}>
                  <TableCell />
                  <TableCell>商品ID</TableCell>
                  <TableCell>商品名</TableCell>
                  <TableCell>変更回数</TableCell>
                  <TableCell>現在のステータス遷移</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">読み込み中...</TableCell>
                  </TableRow>
                ) : (itemHistory?.items ?? []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">データがありません</TableCell>
                  </TableRow>
                ) : (
                  (itemHistory?.items ?? []).map((item) => (
                    <ItemHistoryRow key={item.amazonItemId} item={item} />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mt: 2, mb: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 2 }}>
              全 {itemHistory?.total ?? 0} 件
            </Typography>
            <Pagination
              count={itemTotalPages}
              page={itemPage}
              onChange={(_, v) => setItemPage(v)}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default OperationLogs;
